/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { getDocumentsToPrint, printSelectedDocuments } from "@/requests/document";
import { FILTER_NAMES } from "@/static/filterNames";
import { DataOptions } from "vuetify/types";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DocumentToPrintResponse, DocumentToPrintResponseItem } from "@/models/api/documents/DocumentToPrintResponse";
import _ from "lodash/fp";
import { DocumentToPrint } from "@/models/api/mail/DocumentToPrint";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { getOpenTemporaryFile } from "@/requests/general";

export interface DocumentsPrintStateInterface {
  isDialog: boolean;
  availableDocuments: Array<DocumentToPrintResponseItem>;
  availableDocumentsCount: number;
  availableDocumentsDataTableOptions: DataOptions;
  availableDocumentsFilters: null | Array<CriterionBase>;
  selectedDocuments: Array<DocumentToPrintResponseItem>;
  isPrinting: boolean;
}

export const DocumentsPrintState: DocumentsPrintStateInterface = {
  isDialog: false,
  availableDocuments: [],
  availableDocumentsCount: 0,
  availableDocumentsDataTableOptions: new DefaultDataOptions(),
  availableDocumentsFilters: null,
  selectedDocuments: [],
  isPrinting: false,
};

const DocumentsPrintModule = defineModule({
  state: DocumentsPrintState as DocumentsPrintStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setAvailableDocumentsResponse(state, param: DocumentToPrintResponse) {
      state.availableDocumentsCount = param.totalCount;
      state.availableDocuments = param.items;
    },
    setAvailableDocumentsDataTableOptions(state, param: DataOptions) {
      state.availableDocumentsDataTableOptions = param;
    },
    setAvailableDocumentsFilters(state, param: Array<CriterionBase> | null) {
      state.availableDocumentsFilters = param;
    },
    selectDocument(state, param: DocumentToPrintResponseItem) {
      state.selectedDocuments.push(_.cloneDeep(param));
    },
    unselectDocument(state, documentId: string) {
      state.selectedDocuments.splice(
        state.selectedDocuments.findIndex((v) => v.id === documentId),
        1
      );
    },
    cleanSelectedDocuments(state) {
      state.selectedDocuments = [];
    },
    setIsPrinting(state, param: boolean) {
      state.isPrinting = param;
    },
  },
  actions: {
    async openDialog(context) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.cleanSelectedDocuments();
      commit.setAvailableDocumentsFilters(null);
      commit.setAvailableDocumentsDataTableOptions(new DefaultDataOptions());
      commit.setIsDialog(true);
      await dispatch.downloadDocumentsToPrint();
    },
    async downloadDocumentsToPrint(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setAvailableDocumentsResponse(
        await getDocumentsToPrint({
          count: state.availableDocumentsDataTableOptions.itemsPerPage,
          page: state.availableDocumentsDataTableOptions.page - 1,
          criterions: state.availableDocumentsFilters,
          filterName: FILTER_NAMES.DocumentToPrintFilter,
        })
      );
    },
    async printDocumentsToPrint(context) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedDocuments.length > 0) {
        try {
          commit.setIsPrinting(true);
          await rootDispatch.waitForCompleteAllRequests();
          const fileId = await printSelectedDocuments(
            state.selectedDocuments.map((v) => DocumentToPrint.fromDocumentToPrintResponseItem(v))
          );
          await getOpenTemporaryFile(fileId);
          commit.setIsDialog(false);
        } catch (ex) {
          console.log(ex);
        }
        commit.setIsPrinting(false);
      } else {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("selectDocumentsFirst")), "error"));
      }
    },
    async selectFilteredDocuments(context) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      if (state.availableDocumentsCount > 1000) {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("tooManyDocuments")), "error"));
      } else {
        const documentsToPrint = await getDocumentsToPrint({
          count: state.availableDocumentsCount,
          page: 0,
          criterions: state.availableDocumentsFilters,
          filterName: FILTER_NAMES.DocumentToPrintFilter,
        });
        for (const document of documentsToPrint.items) {
          commit.selectDocument(document);
        }
      }
    },
  },
});

export default DocumentsPrintModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, DocumentsPrintModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, DocumentsPrintModule);
